package com.solevo.dist.components

import androidx.compose.runtime.Composable
import com.solevo.dist.util.StatusInfo
import com.solevo.dist.util.StatusMapper
import com.solevo.dist.util.cssColor
import com.solevo.dist.util.rgbaFromHex
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width

/**
 * Reusable StatusPill component for displaying status badges.
 * Supports both icon-based statuses (invoices) and simple color-based statuses (orders, quotes).
 * 
 * @param text Status label text
 * @param colorHex Color hex code for the status
 * @param iconSrc Optional icon source path (used for invoice statuses)
 * @param useIconForInvoiceStatuses If true, will automatically detect invoice statuses and show icons
 */
@Composable
fun RowScope.StatusPill(
    text: String,
    colorHex: String,
    iconSrc: String? = null,
    useIconForInvoiceStatuses: Boolean = true
) {
    val statusInfo = if (useIconForInvoiceStatuses) {
        StatusMapper.mapStatusText(text)
    } else {
        StatusInfo(text, colorHex, iconSrc)
    }
    
    val finalIconSrc = iconSrc ?: statusInfo.iconSrc
    val finalColorHex = statusInfo.colorHex.takeIf { it != "#888888" } ?: colorHex
    
    val tone = cssColor(finalColorHex)
    val bgTone = rgbaFromHex(finalColorHex, alpha = 0.12)

    Row(
        Modifier
            .align(Alignment.CenterVertically)
            .display(DisplayStyle.LegacyInlineFlex)
            .backgroundColor(bgTone)
            .borderRadius(999.px)
            .padding(leftRight = 10.px, topBottom = 6.px)
            .gap(6.px)
            .color(tone)
    ) {
        if (finalIconSrc != null) {
            // Use Image for invoice status icons
            Image(
                src = finalIconSrc,
                alt = text,
                modifier = Modifier.size(16.px)
            )
        } else {
            // Use SVG checkmark icon for order/quote statuses
            Svg({
                attr("viewBox", "0 0 16 16")
                style {
                    width(16.px); height(16.px)
                    display(DisplayStyle.Block); flexShrink(0)
                }
            }) {
                Path {
                    attr(
                        "d",
                        "M9.64,10.84L9.08,10.28C8.92,10.12 8.73,10.037 8.51,10.03C8.29,10.023 8.1,10.1 7.94,10.26C7.78,10.42 7.7,10.61 7.7,10.83C7.7,11.05 7.78,11.24 7.94,11.4L9.08,12.54C9.24,12.7 9.427,12.78 9.64,12.78C9.853,12.78 10.04,12.7 10.2,12.54L13.06,9.68C13.22,9.52 13.297,9.333 13.29,9.12C13.283,8.907 13.2,8.72 13.04,8.56C12.88,8.413 12.693,8.337 12.48,8.33C12.267,8.323 12.08,8.4 11.92,8.56L9.64,10.84ZM4.8,10.4H5.6C5.827,10.4 6.017,10.323 6.17,10.17C6.323,10.017 6.4,9.827 6.4,9.6C6.4,9.373 6.323,9.183 6.17,9.03C6.017,8.877 5.827,8.8 5.6,8.8H4.8C4.573,8.8 4.383,8.877 4.23,9.03C4.077,9.183 4,9.373 4,9.6C4,9.827 4.077,10.017 4.23,10.17C4.383,10.323 4.573,10.4 4.8,10.4ZM4.8,8H8.8C9.027,8 9.217,7.923 9.37,7.77C9.523,7.617 9.6,7.427 9.6,7.2C9.6,6.973 9.523,6.783 9.37,6.63C9.217,6.477 9.027,6.4 8.8,6.4H4.8C4.573,6.4 4.383,6.477 4.23,6.63C4.077,6.783 4,6.973 4,7.2C4,7.427 4.077,7.617 4.23,7.77C4.383,7.923 4.573,8 4.8,8ZM4.8,5.6H8.8C9.027,5.6 9.217,5.523 9.37,5.37C9.523,5.217 9.6,5.027 9.6,4.8C9.6,4.573 9.523,4.383 9.37,4.23C9.217,4.077 9.027,4 8.8,4H4.8C4.573,4 4.383,4.077 4.23,4.23C4.077,4.383 4,4.573 4,4.8C4,5.027 4.077,5.217 4.23,5.37C4.383,5.523 4.573,5.6 4.8,5.6ZM8,16C6.893,16 5.853,15.79 4.88,15.37C3.907,14.95 3.06,14.38 2.34,13.66C1.62,12.94 1.05,12.093 0.63,11.12C0.21,10.147 0,9.107 0,8C0,6.893 0.21,5.853 0.63,4.88C1.05,3.907 1.62,3.06 2.34,2.34C3.06,1.62 3.907,1.05 4.88,0.63C5.853,0.21 6.893,0 8,0C9.107,0 10.147,0.21 11.12,0.63C12.093,1.05 12.94,1.62 13.66,2.34C14.38,3.06 14.95,3.907 15.37,4.88C15.79,5.853 16,6.893 16,8C16,9.107 15.79,10.147 15.37,11.12C14.95,12.093 14.38,12.94 13.66,13.66C12.94,14.38 12.093,14.95 11.12,15.37C10.147,15.79 9.107,16 8,16Z"
                    )
                    attr("fill", "currentColor")
                }
            }
        }
        
        SpanText(
            text,
            Modifier
                .color(tone)
                .fontSize(12.px)
                .fontWeight(FontWeight.Medium)
                .whiteSpace(WhiteSpace.NoWrap)
        )
    }
}

